import React, { useEffect, useRef } from "react";
import Text from "../../styleguide/Text";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { formInputClasses, formLabelClasses } from "./formControlStyles";

interface Props {
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  textInputClassName?: string;
  name: string;
  placeholder?: string;
  label?: string;
  touched?: boolean;
  error?: string;
  value?: string | number | null;
  onChange?: (value: number | string | null) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  inputProps?: any;
  type?: "text" | "number" | "password" | "textarea" | "tel" | "email";
  disabled?: boolean;
  withVerticalSpacer?: boolean;
  withHorizontalSpacer?: boolean;
  noBottomSpacing?: boolean;
  onBlur?: any;
  onFocus?: any;
  onlyPositiveNumbers?: boolean;
  startComponent?: React.ReactNode;
  endComponent?: React.ReactNode;
  externalInputRef?: React.RefObject<HTMLInputElement>;
  autoFocus?: boolean;
  withUpperCasing?: boolean;
}

const TextInput = ({
  id,
  className,
  style,
  textInputClassName,
  name,
  label,
  touched,
  error,
  value,
  onChange,
  onKeyDown,
  type = "text",
  inputProps,
  disabled,
  withVerticalSpacer,
  withHorizontalSpacer,
  noBottomSpacing = false,
  placeholder,
  onBlur,
  onFocus,
  onlyPositiveNumbers,
  startComponent,
  endComponent,
  externalInputRef,
  autoFocus = false,
  withUpperCasing,
}: Props) => {
  const localRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef || localRef;

  const getValue = () => {
    const inputPropsValue = inputProps?.value;
    if (value !== undefined && value !== null) {
      return value;
    } else if (inputPropsValue !== undefined && inputPropsValue !== null) {
      return inputPropsValue;
    }
    return "";
  };

  const handleOnChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value, selectionStart, selectionEnd } = event.target;

    // we save the cursor position to restore it after modifying the value
    const cursorPosition = { start: selectionStart, end: selectionEnd };

    if (withUpperCasing) {
      // modify the value to be uppercase, the input cursor position will be reseted
      event.target.value = value.toUpperCase();
      // Restore the cursor position after modifying the value
      event.target.setSelectionRange(cursorPosition.start, cursorPosition.end);
    }

    if (onChange) {
      onChange(value);
      return;
    }

    if (inputProps?.onChange) {
      inputProps.onChange(event);
    }
    return;
  };

  const withFormik = () => {
    if (inputProps) {
      return inputProps;
    }
    return;
  };

  const getNativeInputProps = () => {
    if (!inputProps) return undefined;
    const nativeInputProps = { ...inputProps };
    delete nativeInputProps.value;
    delete nativeInputProps.onChange;
    delete nativeInputProps.onBlur;
    delete nativeInputProps.name;
    return nativeInputProps;
  };

  useEffect(() => {
    const element = inputRef.current;
    if (element?.type !== "number") return;
    const handleWheel = () => element.blur();
    element.addEventListener("wheel", handleWheel);
    return () => element.removeEventListener("wheel", handleWheel);
  }, [inputRef]);

  return (
    <div
      className={`${noBottomSpacing ? "" : "mb-7"} w-full ${
        withVerticalSpacer ? "px-1" : ""
      } ${withHorizontalSpacer ? "py-1" : ""} ${className ?? ""}`}
      style={style}>
      {label && (
        <label htmlFor={id ?? name} className={formLabelClasses}>
          <Text variant="body1" component="span" isBold>
            {label}
          </Text>
        </label>
      )}
      {type !== "textarea" && (
        <TextField
          inputRef={inputRef}
          id={id ?? name}
          className={`${formInputClasses} ${noBottomSpacing ? "!mb-0" : ""}`}
          sx={{
            "& fieldset": { border: "none" },
          }}
          autoFocus={autoFocus}
          name={name}
          placeholder={placeholder}
          autoComplete="off"
          type={type}
          disabled={disabled}
          error={Boolean(error && touched)}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyPress={(event) => {
            if (
              onlyPositiveNumbers &&
              (event?.key === "-" || event?.key === "+")
            ) {
              event.preventDefault();
            }
          }}
          onKeyDown={onKeyDown}
          {...withFormik()}
          onChange={handleOnChange}
          value={getValue()}
          inputProps={getNativeInputProps()}
          InputProps={{
            startAdornment: startComponent ? (
              <InputAdornment position="start">{startComponent}</InputAdornment>
            ) : undefined,
            endAdornment: endComponent ? (
              <InputAdornment position="end">{endComponent}</InputAdornment>
            ) : undefined,
          }}
        />
      )}
      {type === "textarea" && (
        <textarea
          ref={inputRef}
          id={id ?? name}
          className={`mb-3 min-h-[120px] w-full resize-y rounded-control border-0 bg-surface-subtle px-4 py-3 font-medium text-muted-dark outline-none ring-inset ring-1 ring-transparent transition focus:bg-white focus:ring-2 ${
            error && touched
              ? "ring-danger/40 focus:ring-danger/40"
              : "focus:ring-action/30"
          } ${textInputClassName ?? ""}`}
          name={name}
          placeholder={placeholder}
          autoComplete="off"
          disabled={disabled}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          {...withFormik()}
          onChange={handleOnChange}
          value={getValue()}
        />
      )}
      {touched && error && (
        <div className="pt-3">
          <Text variant="body1" role="alert" className="text-danger">
            {error}
          </Text>
        </div>
      )}
    </div>
  );
};

export default TextInput;
